// src/tabs/System.jsx
// Nur für die Rolle "System" (Betreiber-Account) sichtbar - siehe Header.jsx/App.js
// (hatRolle('System')). Deckt die SystemEndpoints ab:
//   GET  /api/system/settings        - aktuellen Log-Level + LogRawPayloads lesen
//   PUT  /api/system/settings        - beides live ändern, kein Neustart nötig
//   GET  /api/system/logs/live       - letzte N Zeilen aus dem In-Memory-Ringpuffer
//   GET  /api/system/logs            - Liste der rollierenden Log-Dateien
//   GET  /api/system/logs/{dateiname} - Download einer einzelnen Datei
import React, { useCallback, useEffect, useState } from 'react'
import api from '../api'
import { useAuth } from '../authContext'

const LOG_LEVELS = [
  'Verbose',
  'Debug',
  'Information',
  'Warning',
  'Error',
  'Fatal'
]
const LIVE_POLL_INTERVALL_MS = 5000

function extractErrorMessage (err, fallback) {
  if (err.response) {
    const data = err.response.data
    if (typeof data === 'string' && data.trim() !== '') return data
    if (data?.error) return data.error
    if (data?.title) return data.title
    return fallback
  }
  return 'Netzwerkfehler. Bitte versuche es später erneut.'
}

function formatGroesse (bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatZeitpunkt (iso) {
  const datum = new Date(iso)
  if (Number.isNaN(datum.getTime())) return String(iso)
  return datum.toLocaleString('de-DE')
}

// ===== Einstellungen: Log-Level + LogRawPayloads live umschalten =====
function EinstellungenCard () {
  const { token } = useAuth()
  const [minimumLevel, setMinimumLevel] = useState('Information')
  const [logRawPayloads, setLogRawPayloads] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const ladeEinstellungen = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/system/settings', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setMinimumLevel(response.data.minimumLevel)
      setLogRawPayloads(response.data.logRawPayloads)
    } catch (err) {
      setError(
        extractErrorMessage(err, 'Einstellungen konnten nicht geladen werden.')
      )
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    ladeEinstellungen()
  }, [ladeEinstellungen])

  const speichern = async aenderungen => {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const response = await api.put(
        '/system/settings',
        { minimumLevel, logRawPayloads, ...aenderungen },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setMinimumLevel(response.data.minimumLevel)
      setLogRawPayloads(response.data.logRawPayloads)
      setSuccess('Gespeichert - wirkt sofort, ohne Neustart.')
    } catch (err) {
      setError(extractErrorMessage(err, 'Speichern fehlgeschlagen.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='user-form-card'>
      <h3>Laufzeit-Einstellungen</h3>
      {error && <p className='error-banner'>{error}</p>}
      {success && <p className='success-banner'>{success}</p>}
      {loading ? (
        <p className='placeholder-tab'>Wird geladen…</p>
      ) : (
        <>
          <div className='form-field'>
            <label htmlFor='system-log-level'>Log-Level</label>
            <select
              id='system-log-level'
              value={minimumLevel}
              onChange={e => {
                setMinimumLevel(e.target.value)
                speichern({ minimumLevel: e.target.value })
              }}
              disabled={saving}
            >
              {LOG_LEVELS.map(level => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>
          <div className='form-field'>
            <label className='system-checkbox-label'>
              <input
                type='checkbox'
                checked={logRawPayloads}
                disabled={saving}
                onChange={e => {
                  setLogRawPayloads(e.target.checked)
                  speichern({ logRawPayloads: e.target.checked })
                }}
              />
              <span>Rohdaten loggen (volle Request-/Response-Payloads)</span>
            </label>
            <p className='form-hint'>
              Enthält bei Aktivierung ggf. vollständige Rechnungsdaten der
              Kunden im Log - nur gezielt für die Fehlersuche kurzzeitig
              aktivieren.
            </p>
          </div>
        </>
      )}
    </div>
  )
}

// ===== Live-Ansicht: letzte N Log-Zeilen, pollt automatisch (start-/stoppbar) =====
function LiveLogCard () {
  const { token } = useAuth()
  const [zeilen, setZeilen] = useState([])
  const [anzahl, setAnzahl] = useState(20)
  const [pollingAktiv, setPollingAktiv] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const ladeLiveLog = useCallback(async () => {
    try {
      const response = await api.get('/system/logs/live', {
        params: { count: anzahl },
        headers: { Authorization: `Bearer ${token}` }
      })
      setZeilen(response.data)
      setError(null)
    } catch (err) {
      setError(
        extractErrorMessage(err, 'Live-Log konnte nicht geladen werden.')
      )
    } finally {
      setLoading(false)
    }
  }, [token, anzahl])

  // Einmaliges (Neu-)Laden, sobald sich die Anzahl-Auswahl ändert - unabhängig vom
  // Polling-Status, damit die Auswahl auch bei pausiertem Polling sofort wirkt.
  useEffect(() => {
    setLoading(true)
    ladeLiveLog()
  }, [ladeLiveLog])

  // Automatisches Nachladen nur, solange pollingAktiv true ist.
  useEffect(() => {
    if (!pollingAktiv) return undefined
    const intervalId = setInterval(ladeLiveLog, LIVE_POLL_INTERVALL_MS)
    return () => clearInterval(intervalId)
  }, [pollingAktiv, ladeLiveLog])

  return (
    <div className='user-form-card system-log-card'>
      <div className='rechnung-tabelle-kopf'>
        <h3>
          Live-Log{' '}
          <span
            className={`status-badge ${
              pollingAktiv ? 'status-ok' : 'status-unbekannt'
            }`}
          >
            {pollingAktiv ? 'Live' : 'Pausiert'}
          </span>
        </h3>
        <button
          type='button'
          className='btn'
          onClick={() => setPollingAktiv(aktiv => !aktiv)}
        >
          {pollingAktiv ? '⏸ Pausieren' : '▶ Fortsetzen'}
        </button>
      </div>
      <div className='form-field' style={{ maxWidth: 160 }}>
        <label htmlFor='system-live-anzahl'>Anzahl Zeilen</label>
        <select
          id='system-live-anzahl'
          value={anzahl}
          onChange={e => setAnzahl(Number(e.target.value))}
        >
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>
      {error && <p className='error-banner'>{error}</p>}
      {loading ? (
        <p className='placeholder-tab'>Wird geladen…</p>
      ) : zeilen.length === 0 ? (
        <p className='placeholder-tab'>
          Noch keine Log-Einträge in diesem Lauf.
        </p>
      ) : (
        <pre className='system-live-log'>{zeilen.join('\n')}</pre>
      )}
    </div>
  )
}

// ===== Liste der Log-Dateien mit Download =====
function LogDateienCard () {
  const { token } = useAuth()
  const [dateien, setDateien] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const ladeDateien = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/system/logs', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setDateien(response.data)
    } catch (err) {
      setError(
        extractErrorMessage(
          err,
          'Liste der Log-Dateien konnte nicht geladen werden.'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    ladeDateien()
  }, [ladeDateien])

  const herunterladen = async dateiname => {
    setError(null)
    try {
      const response = await api.get(
        `/system/logs/${encodeURIComponent(dateiname)}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      const url = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = dateiname
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(
        extractErrorMessage(err, `Download von "${dateiname}" fehlgeschlagen.`)
      )
    }
  }

  return (
    <div className='user-form-card system-log-card'>
      <div className='rechnung-tabelle-kopf'>
        <h3>Log-Dateien</h3>
        <button
          type='button'
          className='btn'
          onClick={ladeDateien}
          disabled={loading}
        >
          Aktualisieren
        </button>
      </div>
      {error && <p className='error-banner'>{error}</p>}
      {loading ? (
        <p className='placeholder-tab'>Wird geladen…</p>
      ) : dateien.length === 0 ? (
        <p className='placeholder-tab'>Keine Log-Dateien vorhanden.</p>
      ) : (
        <table className='users-tabelle'>
          <thead>
            <tr>
              <th>Datei</th>
              <th>Größe</th>
              <th>Geändert</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {dateien.map(datei => (
              <tr key={datei.dateiname}>
                <td>{datei.dateiname}</td>
                <td>{formatGroesse(datei.groesseBytes)}</td>
                <td>{formatZeitpunkt(datei.geaendertAmUtc)}</td>
                <td className='users-tabelle-aktionen'>
                  <button
                    type='button'
                    className='btn-icon'
                    title={`"${datei.dateiname}" herunterladen`}
                    onClick={() => herunterladen(datei.dateiname)}
                  >
                    ⬇️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function System () {
  return (
    <div className='tab-content'>
      <div className='users-tab-sections'>
        <EinstellungenCard />
        <LiveLogCard />
        <LogDateienCard />
      </div>
    </div>
  )
}

export default System
